/**
 * veodiagram-issues#25 — Pre-analysis review modal.
 *
 * Shown after the user clicks Analyze. Lets them inspect and
 * correct the system's auto-classifications BEFORE the pipeline
 * runs. Two classifications per file:
 *
 *   - included / excluded (checkbox)
 *   - role: frontend | backend | shared | config | test | unknown
 *
 * Folder-level toggle cascades to children with explicit overrides
 * preserved. Hover an excluded file to see why the heuristic
 * excluded it. "Reset to defaults" wipes user edits.
 *
 * On confirm: PUTs the user's classifications, then triggers
 * /analyze. /analyze's findSourceFiles reads `project_file_scope`
 * and honors the user-included set.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../services/api';

type FileRole = 'frontend' | 'backend' | 'shared' | 'config' | 'test' | 'unknown';

interface DiscoveredFile {
  path: string;
  autoIncluded: boolean;
  autoRole: FileRole;
  autoReason: string;
}

interface ScopeRow {
  path: string;
  included: boolean;
  role: FileRole;
  source: 'auto' | 'ai' | 'user';
  autoReason: string;
}

interface PreAnalysisReviewProps {
  isOpen: boolean;
  projectId: string;
  onCancel: () => void;
  /** Called after PUT completes — caller fires /analyze next. */
  onConfirm: () => void;
}

const ROLE_BADGE_COLORS: Record<FileRole, string> = {
  frontend: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  backend: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  shared: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  config: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  test: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  unknown: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const ALL_ROLES: FileRole[] = ['frontend', 'backend', 'shared', 'config', 'test', 'unknown'];

export function PreAnalysisReview({
  isOpen,
  projectId,
  onCancel,
  onConfirm,
}: PreAnalysisReviewProps) {
  const [discovering, setDiscovering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ScopeRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set([''])); // root expanded by default
  const [filterRole, setFilterRole] = useState<FileRole | 'all'>('all');
  const [filterIncluded, setFilterIncluded] = useState<'all' | 'included' | 'excluded'>('all');

  // Load discover + persisted scope on open. Merge: persisted user
  // entries override auto-discovered defaults.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setDiscovering(true);
    setError(null);
    (async () => {
      try {
        const [disc, persisted] = await Promise.all([
          api.discoverFileScope(projectId),
          api.getFileScope(projectId).catch(() => ({ entries: [] })),
        ]);
        if (cancelled) return;
        const byPath = new Map<string, ScopeRow>();
        for (const f of disc.files as DiscoveredFile[]) {
          byPath.set(f.path, {
            path: f.path,
            included: f.autoIncluded,
            role: f.autoRole,
            source: 'auto',
            autoReason: f.autoReason,
          });
        }
        for (const e of persisted.entries) {
          const existing = byPath.get(e.filePath);
          byPath.set(e.filePath, {
            path: e.filePath,
            included: e.included,
            role: e.role,
            source: e.source,
            autoReason: existing?.autoReason ?? e.autoReason ?? '',
          });
        }
        setRows([...byPath.values()].sort((a, b) => a.path.localeCompare(b.path)));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to discover files');
      } finally {
        if (!cancelled) setDiscovering(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId]);

  // Build a folder tree from the flat row list. Keyed by folder
  // path (relative). The map's empty-string key is the project root.
  const tree = useMemo(() => buildTree(rows), [rows]);

  // Compute the set of paths matching active filters. We compare
  // by path (not object identity) because `buildTree` copies rows.
  const filteredPaths = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (filterRole !== 'all' && r.role !== filterRole) continue;
      if (filterIncluded === 'included' && !r.included) continue;
      if (filterIncluded === 'excluded' && r.included) continue;
      set.add(r.path);
    }
    return set;
  }, [rows, filterRole, filterIncluded]);

  const summary = useMemo(() => {
    let included = 0;
    let excluded = 0;
    const byRole: Record<FileRole, number> = {
      frontend: 0, backend: 0, shared: 0, config: 0, test: 0, unknown: 0,
    };
    for (const r of rows) {
      if (r.included) included++;
      else excluded++;
      byRole[r.role]++;
    }
    return { included, excluded, total: rows.length, byRole };
  }, [rows]);

  const toggleFolder = useCallback((dir: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(dir)) next.delete(dir);
      else next.add(dir);
      return next;
    });
  }, []);

  const toggleFileIncluded = useCallback((path: string) => {
    setRows(prev => prev.map(r => r.path === path ? { ...r, included: !r.included, source: 'user' as const } : r));
  }, []);

  const toggleFolderIncluded = useCallback((dir: string) => {
    setRows(prev => {
      const prefix = dir === '' ? '' : dir + '/';
      // Determine target state: if any descendant is excluded, include all; else exclude all.
      const descendants = prev.filter(r => dir === '' || r.path.startsWith(prefix));
      const anyExcluded = descendants.some(r => !r.included);
      const target = anyExcluded;
      return prev.map(r =>
        (dir === '' || r.path.startsWith(prefix))
          ? { ...r, included: target, source: 'user' as const }
          : r,
      );
    });
  }, []);

  const setFileRole = useCallback((path: string, role: FileRole) => {
    setRows(prev => prev.map(r => r.path === path ? { ...r, role, source: 'user' as const } : r));
  }, []);

  const handleReset = useCallback(async () => {
    try {
      setSaving(true);
      await api.resetFileScope(projectId);
      // Reload from discover to get fresh auto state.
      const disc = await api.discoverFileScope(projectId);
      setRows(
        disc.files
          .map((f: DiscoveredFile) => ({
            path: f.path,
            included: f.autoIncluded,
            role: f.autoRole,
            source: 'auto' as const,
            autoReason: f.autoReason,
          }))
          .sort((a, b) => a.path.localeCompare(b.path)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset');
    } finally {
      setSaving(false);
    }
  }, [projectId]);

  const handleConfirm = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await api.saveFileScope(
        projectId,
        rows.map(r => ({ path: r.path, included: r.included, role: r.role })),
      );
      onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save scope');
      setSaving(false);
    }
  }, [projectId, rows, onConfirm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-5xl mx-4 max-h-[85vh] bg-background border border-border rounded-lg shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold">Review files before analysis</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {discovering
                ? 'Discovering files…'
                : `${summary.total} files found · ${summary.included} included · ${summary.excluded} excluded`}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex">
          {/* Tree */}
          <div className="flex-1 overflow-auto p-4">
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded text-sm">
                {error}
              </div>
            )}
            {discovering ? (
              <div className="text-center text-muted-foreground py-8">
                Reading project files…
              </div>
            ) : (
              <FileTreeView
                tree={tree}
                expanded={expanded}
                onToggleFolder={toggleFolder}
                onToggleFolderIncluded={toggleFolderIncluded}
                onToggleFileIncluded={toggleFileIncluded}
                onSetFileRole={setFileRole}
                rowFilter={(r) => filteredPaths.has(r.path)}
                showFilter={filterRole !== 'all' || filterIncluded !== 'all'}
              />
            )}
          </div>

          {/* Summary sidebar */}
          <div className="w-64 border-l border-border p-4 overflow-auto bg-muted/30 text-sm">
            <h3 className="font-semibold mb-3">Filters</h3>
            <label className="block mb-2 text-xs uppercase text-muted-foreground">Role</label>
            <select
              className="w-full px-2 py-1 mb-3 border border-border rounded bg-background text-sm"
              value={filterRole}
              onChange={e => setFilterRole(e.target.value as FileRole | 'all')}
            >
              <option value="all">All roles</option>
              {ALL_ROLES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <label className="block mb-2 text-xs uppercase text-muted-foreground">Inclusion</label>
            <select
              className="w-full px-2 py-1 mb-4 border border-border rounded bg-background text-sm"
              value={filterIncluded}
              onChange={e => setFilterIncluded(e.target.value as 'all' | 'included' | 'excluded')}
            >
              <option value="all">All</option>
              <option value="included">Included only</option>
              <option value="excluded">Excluded only</option>
            </select>

            <h3 className="font-semibold mb-3">By role</h3>
            <ul className="space-y-1 mb-4">
              {ALL_ROLES.map(role => (
                <li key={role} className="flex justify-between">
                  <span className="capitalize">{role}</span>
                  <span className="text-muted-foreground">{summary.byRole[role]}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              disabled={saving || discovering}
              onClick={handleReset}
              className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent disabled:opacity-50"
            >
              Reset to defaults
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm border border-border rounded hover:bg-accent disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || discovering || rows.length === 0}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : `Run analysis on ${summary.included} files`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Folder tree
// ─────────────────────────────────────────────────────────────────────

interface FolderNode {
  dir: string;             // relative folder path; '' = root
  subfolders: Map<string, FolderNode>;
  files: ScopeRow[];
}

function buildTree(rows: ScopeRow[]): FolderNode {
  const root: FolderNode = { dir: '', subfolders: new Map(), files: [] };
  for (const row of rows) {
    const parts = row.path.split('/');
    const fileName = parts.pop()!; // last segment
    let cur = root;
    let accum = '';
    for (const part of parts) {
      accum = accum ? `${accum}/${part}` : part;
      if (!cur.subfolders.has(part)) {
        cur.subfolders.set(part, { dir: accum, subfolders: new Map(), files: [] });
      }
      cur = cur.subfolders.get(part)!;
    }
    cur.files.push({ ...row, path: row.path });
    void fileName;
  }
  return root;
}

interface FileTreeViewProps {
  tree: FolderNode;
  expanded: Set<string>;
  onToggleFolder: (dir: string) => void;
  onToggleFolderIncluded: (dir: string) => void;
  onToggleFileIncluded: (path: string) => void;
  onSetFileRole: (path: string, role: FileRole) => void;
  rowFilter: (r: ScopeRow) => boolean;
  showFilter: boolean;
}

function FileTreeView(props: FileTreeViewProps) {
  return <FolderRow node={props.tree} depth={0} {...props} />;
}

function FolderRow({
  node,
  depth,
  expanded,
  onToggleFolder,
  onToggleFolderIncluded,
  onToggleFileIncluded,
  onSetFileRole,
  rowFilter,
  showFilter,
  tree: _tree,
}: { node: FolderNode; depth: number } & FileTreeViewProps) {
  const isOpen = expanded.has(node.dir);
  const allFilesInTree = useMemo(() => collectFiles(node), [node]);
  const visibleFiles = showFilter ? allFilesInTree.filter(rowFilter) : allFilesInTree;
  const includedCount = visibleFiles.filter(r => r.included).length;
  const totalCount = visibleFiles.length;

  if (totalCount === 0 && showFilter) return null;

  const folderName = node.dir === '' ? '(project root)' : node.dir.split('/').slice(-1)[0];

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 px-1 hover:bg-accent/50 rounded cursor-pointer text-sm"
        style={{ paddingLeft: `${depth * 1.25 + 0.25}rem` }}
        onClick={() => onToggleFolder(node.dir)}
      >
        <span className="w-3 text-muted-foreground">{isOpen ? '▾' : '▸'}</span>
        <input
          type="checkbox"
          ref={el => { if (el) el.indeterminate = includedCount > 0 && includedCount < totalCount; }}
          checked={includedCount === totalCount && totalCount > 0}
          onChange={() => onToggleFolderIncluded(node.dir)}
          onClick={e => e.stopPropagation()}
        />
        <span className="font-medium">{folderName}</span>
        <span className="text-xs text-muted-foreground ml-1">
          ({includedCount}/{totalCount})
        </span>
      </div>
      {isOpen && (
        <div>
          {[...node.subfolders.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([name, sub]) => (
              <FolderRow
                key={name}
                node={sub}
                depth={depth + 1}
                expanded={expanded}
                onToggleFolder={onToggleFolder}
                onToggleFolderIncluded={onToggleFolderIncluded}
                onToggleFileIncluded={onToggleFileIncluded}
                onSetFileRole={onSetFileRole}
                rowFilter={rowFilter}
                showFilter={showFilter}
                tree={_tree}
              />
            ))}
          {node.files
            .filter(showFilter ? rowFilter : () => true)
            .sort((a, b) => a.path.localeCompare(b.path))
            .map(file => (
              <FileRow
                key={file.path}
                file={file}
                depth={depth + 1}
                onToggleFileIncluded={onToggleFileIncluded}
                onSetFileRole={onSetFileRole}
              />
            ))}
        </div>
      )}
    </div>
  );
}

function FileRow({
  file,
  depth,
  onToggleFileIncluded,
  onSetFileRole,
}: {
  file: ScopeRow;
  depth: number;
  onToggleFileIncluded: (path: string) => void;
  onSetFileRole: (path: string, role: FileRole) => void;
}) {
  const fileName = file.path.split('/').slice(-1)[0];
  return (
    <div
      className="flex items-center gap-2 py-1 px-1 hover:bg-accent/50 rounded text-sm"
      style={{ paddingLeft: `${depth * 1.25 + 0.25}rem` }}
      title={file.autoReason || ''}
    >
      <span className="w-3" />
      <input
        type="checkbox"
        checked={file.included}
        onChange={() => onToggleFileIncluded(file.path)}
      />
      <span className={file.included ? '' : 'text-muted-foreground line-through'}>
        {fileName}
      </span>
      <select
        value={file.role}
        onChange={e => onSetFileRole(file.path, e.target.value as FileRole)}
        className={`ml-auto px-1.5 py-0.5 text-xs rounded border-0 ${ROLE_BADGE_COLORS[file.role]}`}
      >
        {ALL_ROLES.map(r => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
      {file.source !== 'auto' && (
        <span className="text-xs text-muted-foreground" title={`source: ${file.source}`}>
          ✎
        </span>
      )}
    </div>
  );
}

function collectFiles(node: FolderNode): ScopeRow[] {
  const out: ScopeRow[] = [...node.files];
  for (const sub of node.subfolders.values()) {
    out.push(...collectFiles(sub));
  }
  return out;
}
